import {
  useCallback,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import type { AppDispatch } from '@/store/store';

import {
  forgotPasswordThunk,
  loginThunk,
  signupThunk,
} from '../store/authThunk';

import { useAuthRedirect } from './useAuthRedirect';

interface UseAuthFormOptions {
  pendingJobId: string | null;
  onSuccess: () => void;
}

export function useAuthForm({
  pendingJobId,
  onSuccess,
}: UseAuthFormOptions) {
  const dispatch = useDispatch<AppDispatch>();
  const { redirectUser } = useAuthRedirect();

  const [error, setError] = useState<
    string | null
  >(null);

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const handleSuccess = (
    user: ReturnType<
      typeof loginThunk.fulfilled
    >['payload']['user']
  ) => {
    if (pendingJobId) {
      onSuccess();
      return;
    }

    redirectUser(user);
    onSuccess();
  };

  const submitLogin = async (
    email: string,
    password: string
  ) => {
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await dispatch(
        loginThunk({
          email,
          password,
        })
      ).unwrap();

      handleSuccess(result.user);
    } catch (err) {
      setError(
        (err as string) ||
          'Login failed'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitSignup = async (
    name: string,
    email: string,
    password: string
  ) => {
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await dispatch(
        signupThunk({
          name,
          email,
          password,
        })
      ).unwrap();

      handleSuccess(result.user);
    } catch (err) {
      setError(
        (err as string) ||
          'Signup failed'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitForgotPassword = async (
    email: string
  ) => {
    setError(null);
    setIsSubmitting(true);

    try {
      await dispatch(
        forgotPasswordThunk({
          email,
        })
      ).unwrap();
    } catch (err) {
      setError(
        (err as string) ||
          'Password reset request failed'
      );

      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return {
    error,
    isSubmitting,
    submitLogin,
    submitSignup,
    submitForgotPassword,
    resetError,
  };
}